import React from 'react';
import Modal from 'react-modal';
import { Field, Form, Formik } from 'formik';
import { Oval } from 'react-loader-spinner';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { toggleShowAddDriverModal, createDriverV2, getDriversV2 } from '../../DriversV2/driversV2Slice';
import { ReactComponent as Close } from '../../../assets/images/close.svg';

import './AddDriverModal.scss';

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: "#FFFFFF"
  },
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  address: Yup.string()
    .required('Address is required'),
  zip: Yup.string()
    .required('Zip is required'),
  dlExpiration: Yup.string(),
  dotPhysicalExpiration: Yup.string(),
  startTime: Yup.string()
    .matches(/^([01]\d|2[0-3]):[0-5]\d$/, 'Start time must be HH:MM (24-hour)')
    .required('Start time is required'),
});

export interface AddDriverFormModel {
  email: string;
  address: string;
  zip: string;
  dlExpiration: string;
  dotPhysicalExpiration: string;
  startTime: string;
};

const initialValues: AddDriverFormModel = {
  email: '',
  address: '',
  zip: '',
  dlExpiration: '',
  dotPhysicalExpiration: '',
  startTime: '06:00',
};

export function AddDriverModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(state => state.driversV2.showAddDriverModal);
  const isLoading = useAppSelector(state => state.driversV2.createDriver.loading);
  const error = useAppSelector(state => state.driversV2.createDriver.error);
  const driver = useAppSelector(state => !!state.driversV2.createDriver.value);

  React.useEffect(() => {
    if (!isLoading && !!error) {
      toast.error('Something went wrong. Please try again.');
    }

    if (!isLoading && !error && driver) {
      toast.success('Successfully added driver.');
      dispatch(getDriversV2());
    }
  }, [driver, error]);

  const toggleModal = () => {
    dispatch(toggleShowAddDriverModal());
  }

  const onSubmit = (form: AddDriverFormModel) => {
    dispatch(createDriverV2(form));
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggleModal}
      style={modalStyles}
    >
      <div className='add-driver-modal-component'>
        <Close className='close-button' onClick={toggleModal}/>

        <div className='title'>Add Driver</div>
        
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className='add-driver-form'>
              <div className='field-container'>
                <div className='label'>Email:</div>
                <Field id='email' name='email' className='text-input' placeholder='Email'/>
                {errors.email && touched.email && <div className='form-error'>{errors.email}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>Address:</div>
                <Field id='address' name='address' className='text-input' placeholder='Address'/>
                {errors.address && touched.address && <div className='form-error'>{errors.address}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>Zip:</div>
                <Field id='zip' name='zip' className='text-input' placeholder='Zip'/>
                {errors.zip && touched.zip && <div className='form-error'>{errors.zip}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>DL Expiration:</div>
                <Field id='dlExpiration' name='dlExpiration' type='date' className='text-input'/>
              </div>

              <div className='field-container'>
                <div className='label'>DOT Physical Expiration:</div>
                <Field id='dotPhysicalExpiration' name='dotPhysicalExpiration' type='date' className='text-input'/>
              </div>

              <div className='field-container'>
                <div className='label'>Start Time:</div>
                <Field id='startTime' name='startTime' type='time' className='text-input'/>
                {errors.startTime && touched.startTime && <div className='form-error'>{errors.startTime}</div>}
              </div>

              <button type='submit' className='add-driver-button' disabled={isLoading}>
                Add Driver
                {isLoading && <div style={{ marginLeft: 8}}><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>}
              </button>

              {!!error && (
                <div className='error-text'>
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
